import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';

interface ObservationRow {
  readonly observationId: string;
  readonly observationCode: string;
  readonly observationType: string;
  readonly severity: string;
  readonly riskLevel: string;
  readonly status: string;
}

export function M21ObservationManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ObservationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [auditExecutionId, setAuditExecutionId] = useState('');
  const [observationCode, setObservationCode] = useState('');
  const [observationType, setObservationType] = useState('');
  const [observationCategory, setObservationCategory] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [riskLevel, setRiskLevel] = useState('MEDIUM');
  const [description, setDescription] = useState('');

  function load(): void {
    apiRequest<{ data: ObservationRow[] }>('/v1/audit/observations', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: ObservationRow[] }>('/v1/audit/observations', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!auditExecutionId.trim() || !observationCode.trim() || !observationType.trim()
      || !observationCategory.trim() || !description.trim()) {
      setActionError(t('m21.observation.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/audit/observations', {
        method: 'POST',
        token,
        body: {
          auditExecutionId: auditExecutionId.trim(),
          observationCode: observationCode.trim(),
          observationType: observationType.trim(),
          observationCategory: observationCategory.trim(),
          severity,
          riskLevel,
          description: description.trim(),
        },
      });
      setAuditExecutionId(''); setObservationCode(''); setObservationType('');
      setObservationCategory(''); setSeverity('MEDIUM'); setRiskLevel('MEDIUM'); setDescription('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function closeObservation(id: string): Promise<void> {
    setActionError(null);
    setBusyId(`close-${id}`);
    try {
      await apiRequest(`/v1/audit/observations/${id}`, { method: 'PATCH', token, body: { status: 'CLOSED' } });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.observation.loading')}</p>;
  return (
    <main aria-label="observation-management">
      <h1>{t('m21.observation.title')}</h1>

      <form aria-label="observation-create-form" onSubmit={handleCreate}>
        <RecordPicker<{ auditExecutionId: string; executionCode: string; areaAudited: string }>
          endpoint="/v1/audit/executions"
          idField="auditExecutionId"
          label={t('m21.observation.formExecution')}
          testId="observation-execution"
          value={auditExecutionId}
          optionLabel={(x) => `${x.executionCode} \u2014 ${x.areaAudited}`}
          onPick={(id) => setAuditExecutionId(id)}
        />
        <label>{t('m21.observation.formCode')}<input aria-label="Observation code" value={observationCode} onChange={(e) => setObservationCode(e.target.value)} /></label>
        <label>{t('m21.observation.formType')}<input aria-label="Observation type" value={observationType} onChange={(e) => setObservationType(e.target.value)} /></label>
        <label>{t('m21.observation.formCategory')}<input aria-label="Category" value={observationCategory} onChange={(e) => setObservationCategory(e.target.value)} /></label>
        <label>{t('m21.observation.formSeverity')}
          <select aria-label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="CRITICAL">CRITICAL</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
            <option value="INFORMATIONAL">INFORMATIONAL</option>
          </select>
        </label>
        <label>{t('m21.observation.formRiskLevel')}
          <select aria-label="Risk level" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
            <option value="CRITICAL">CRITICAL</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </label>
        <label>{t('m21.observation.formDescription')}<input aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        <button type="submit" disabled={busyId === 'create'}>{t('m21.observation.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="observations-table">
        <thead><tr><th>{t('m21.observation.colCode')}</th><th>{t('m21.observation.colType')}</th><th>{t('m21.observation.colSeverity')}</th><th>{t('m21.observation.colRiskLevel')}</th><th>{t('m21.observation.colStatus')}</th><th>{t('m21.observation.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.observationId}>
              <td>{o.observationCode}</td>
              <td>{o.observationType}</td>
              <td>{o.severity}</td>
              <td>{o.riskLevel}</td>
              <td>{o.status}</td>
              <td>
                {o.status.toUpperCase() !== 'CLOSED' && (
                  <button
                    type="button"
                    aria-label={`close-${o.observationId}`}
                    disabled={busyId === `close-${o.observationId}`}
                    onClick={() => closeObservation(o.observationId)}
                  >{t('m21.observation.close')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
